/**
 * A purchase's details (agent wallet P3 task 17; the founder-approved pay screens,
 * mockups/agent-wallet-pay-v2-2026-10-16.html): what the shop's page described, as typed fields, so the person's
 * purchase card can draw a flight, a hotel stay or a cart as a real component instead of one line of text. buy sends
 * them; the box parses them with this schema before it asks the server, the server parses them again (it never trusts
 * the box) and stores them on the purchase at its request, and the purchase view returns them.
 *
 * THE ONE RULE: details never carry the total. The amount, the currency, the shop and its host are Vestra's own record
 * (the purchase row). Details are drawn as the shop's page described them and can never change what is held or paid. A
 * cart's own sum (cartTotalMinor) is only compared with the server's amount, never used in its place.
 *
 * Every string is trimmed and bounded, unknown keys are stripped, and the whole value serializes to at most
 * PURCHASE_DETAILS_MAX_BYTES (the migration's CHECK on agent_purchases.details is the database's own, looser bound). An
 * image is a plain image address: https, no user or password, the default port, a named host with a dot (never an IP
 * or localhost), stored as new URL reads it back. `priceNote` is the shop page's own price line, drawn as written and
 * never read as a total.
 */
import { z } from 'zod';

/** The largest serialized details, in bytes of UTF-8 JSON. */
export const PURCHASE_DETAILS_MAX_BYTES = 8192;
/** The longest image address a card draws. */
const IMAGE_URL_MAX = 500;
const DAY_MS = 86_400_000;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d$/;
const CLOCK_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** A 'YYYY-MM-DD' that names a real day, as UTC midnight in ms; NaN for anything else ('2025-02-30' included). */
function dayMs(date: string): number {
  if (!DATE_RE.test(date)) return NaN;
  const ms = Date.parse(`${date}T00:00:00Z`);
  return Number.isFinite(ms) && new Date(ms).toISOString().slice(0, 10) === date ? ms : NaN;
}

const IPV4_HOST_RE = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * A plain image address: https, no user or password, the default port, and a named host of two or more labels, never
 * an IP (new URL turns every IPv4 spelling into dotted quads and keeps an IPv6 one in brackets) or localhost.
 */
function isPlainImageAddress(url: string): boolean {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return false;
  }
  if (u.protocol !== 'https:' || u.username !== '' || u.password !== '' || u.port !== '') return false;
  const host = u.hostname.replace(/\.$/, '');
  if (host.startsWith('[') || IPV4_HOST_RE.test(host)) return false;
  if (host === 'localhost' || host.endsWith('.localhost')) return false;
  return host.split('.').length >= 2;
}

const text = (max: number) => z.string().trim().min(1).max(max);
const whole = (min: number, max: number) => z.number().int().min(min).max(max);
const minorUnits = () => z.number().int().nonnegative();
/** A calendar day, 'YYYY-MM-DD'. */
const day = () => z.string().trim().regex(DATE_RE).refine((s) => Number.isFinite(dayMs(s)), 'a real date, YYYY-MM-DD');
/** A time of day, 'HH:mm'. */
const clock = () => z.string().trim().regex(CLOCK_RE);
/** An airport's local time as the shop shows it, 'YYYY-MM-DDTHH:mm' (no zone). */
const localDateTime = () =>
  z
    .string()
    .trim()
    .regex(LOCAL_DATE_TIME_RE)
    .refine((s) => Number.isFinite(dayMs(s.slice(0, 10))), 'a real date and time, YYYY-MM-DDTHH:mm');
/** An image to draw: a plain image address (isPlainImageAddress), stored as new URL reads it, at most IMAGE_URL_MAX characters. */
const image = () =>
  z
    .string()
    .trim()
    .refine(isPlainImageAddress, { message: 'a plain https image address on a named host', abort: true })
    .overwrite((s) => new URL(s).href)
    .max(IMAGE_URL_MAX);
/** The shop page's own price line as shown ("Fare ₹4,120 · Taxes and fees ₹700"); drawn, never read as a total. */
const priceNote = () => text(80).optional();
/** A fee or a discount: what the shop calls it and its amount in the shop's minor units. */
const MoneyLine = z.object({ label: text(40), minor: minorUnits() });

const Airport = z.object({
  code: z.string().trim().regex(/^[A-Z]{3}$/),
  city: text(60),
  terminal: text(20).optional(),
});

const FlightLeg = z.object({
  carrier: text(60),
  carrierCode: z.string().trim().regex(/^[A-Z0-9]{2,3}$/).optional(),
  flightNumber: text(10),
  from: Airport,
  to: Airport,
  departLocal: localDateTime(),
  arriveLocal: localDateTime(),
  durationMin: whole(1, 2000),
  stops: whole(0, 3),
});

export const FlightDetailsSchema = z.object({
  kind: z.literal('flight'),
  legs: z.array(FlightLeg).min(1).max(4),
  cabin: text(40).optional(),
  passengers: z.object({ adults: whole(1, 9), children: whole(0, 9).optional(), infants: whole(0, 9).optional() }),
  baggage: z.object({ checkedKg: whole(0, 64).optional(), cabinKg: whole(0, 20).optional() }).optional(),
  priceNote: priceNote(),
});

export const StayDetailsSchema = z
  .object({
    kind: z.literal('stay'),
    property: text(120),
    area: text(120).optional(),
    stars: whole(1, 5).optional(),
    score: z.number().min(0).max(10).multipleOf(0.1).optional(),
    reviews: z.number().int().nonnegative().optional(),
    checkIn: day(),
    checkOut: day(),
    nights: whole(1, 60),
    checkInFrom: clock().optional(),
    checkOutBy: clock().optional(),
    room: text(120),
    /** What comes with the room, e.g. "Breakfast for two". */
    board: text(80).optional(),
    guests: z.object({ adults: whole(1, 12), children: whole(0, 12).optional() }),
    /** The last day it can be cancelled for free: on or before checkIn. */
    freeCancellationUntil: day().optional(),
    photoUrl: image().optional(),
    priceNote: priceNote(),
  })
  .superRefine((stay, ctx) => {
    const inMs = dayMs(stay.checkIn);
    const outMs = dayMs(stay.checkOut);
    // A date that is not a real day already has its own issue.
    if (!Number.isFinite(inMs) || !Number.isFinite(outMs)) return;
    if (outMs <= inMs) {
      ctx.addIssue({ code: 'custom', path: ['checkOut'], message: 'checkOut must be after checkIn' });
    } else if (Math.round((outMs - inMs) / DAY_MS) !== stay.nights) {
      ctx.addIssue({ code: 'custom', path: ['nights'], message: 'nights must be the number of days from checkIn to checkOut' });
    }
    if (stay.freeCancellationUntil !== undefined && dayMs(stay.freeCancellationUntil) > inMs) {
      ctx.addIssue({ code: 'custom', path: ['freeCancellationUntil'], message: 'free cancellation must end on or before checkIn' });
    }
  });

export const CartDetailsSchema = z.object({
  kind: z.literal('cart'),
  items: z
    .array(
      z.object({
        name: text(120),
        /** The pack or size, e.g. "1 L carton". */
        variant: text(60).optional(),
        qty: whole(1, 99),
        /** One unit's price in the shop's minor units (paise or cents). */
        unitMinor: minorUnits(),
        imageUrl: image().optional(),
      }),
    )
    .min(1)
    .max(40),
  /** Handling, delivery and the like, in the shop's minor units; a free one is 0. */
  fees: z.array(MoneyLine).max(10).optional(),
  /** Coupons and offers the page takes off, in the shop's minor units, each as a positive amount. */
  discounts: z.array(MoneyLine).max(6).optional(),
  deliverTo: z.object({ label: text(40), line: text(120) }).optional(),
  /** When it arrives, as the shop says it: "11 min", "Thu, 6 Nov". */
  etaText: text(40).optional(),
  priceNote: priceNote(),
});

const serializedBytes = (value: unknown): number => new TextEncoder().encode(JSON.stringify(value)).length;

/** Every field's own rule, without the size cap. */
const PurchaseDetailsShape = z.discriminatedUnion('kind', [FlightDetailsSchema, StayDetailsSchema, CartDetailsSchema]);

/** A purchase's details: a flight, a hotel stay or a cart, never its total. */
export const PurchaseDetailsSchema = PurchaseDetailsShape.refine(
  (details) => serializedBytes(details) <= PURCHASE_DETAILS_MAX_BYTES,
  `details must be at most ${PURCHASE_DETAILS_MAX_BYTES} bytes`,
);

export type PurchaseDetails = z.output<typeof PurchaseDetailsSchema>;
export type FlightDetails = z.output<typeof FlightDetailsSchema>;
export type StayDetails = z.output<typeof StayDetailsSchema>;
export type CartDetails = z.output<typeof CartDetailsSchema>;

/**
 * What a cart's own lines add up to: every item's qty x unitMinor, plus every fee, less every discount, never below 0.
 * Compared with the server's amount, never shown as the total.
 */
export function cartTotalMinor(details: CartDetails): number {
  const sum = (lines: ReadonlyArray<{ minor: number }> | undefined) => (lines ?? []).reduce((total, line) => total + line.minor, 0);
  const items = details.items.reduce((total, item) => total + item.qty * item.unitMinor, 0);
  return Math.max(0, items + sum(details.fees) - sum(details.discounts));
}

/** The details without their images (a cart's item images, a stay's photo): the long part of a big value. */
export function purchaseDetailsWithoutImages(details: PurchaseDetails): PurchaseDetails {
  if (details.kind === 'cart') return { ...details, items: details.items.map(({ imageUrl: _image, ...item }) => item) };
  if (details.kind === 'stay') {
    const { photoUrl: _photo, ...stay } = details;
    return stay;
  }
  return details;
}

/**
 * buy's details read on the box: PurchaseDetailsSchema, except that when every field passes and only the size cap
 * fails, the images are dropped and the rest kept (a card without thumbnails still shows what is bought).
 */
export function parsePurchaseDetails(value: unknown) {
  const parsed = PurchaseDetailsSchema.safeParse(value);
  if (parsed.success) return parsed;
  const shape = PurchaseDetailsShape.safeParse(value);
  return shape.success ? PurchaseDetailsSchema.safeParse(purchaseDetailsWithoutImages(shape.data)) : parsed;
}
